import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { toast } from 'react-toastify';
import { courseAPI, syllabusAPI } from '../utils/api';

const CourseDetail = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [syllabus, setSyllabus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedUnits, setExpandedUnits] = useState({});
    const [durationType, setDurationType] = useState('yearly');

    useEffect(() => {
        fetchCourseDetails();
    }, [courseId]);

    const fetchCourseDetails = async () => {
        try {
            setLoading(true);

            // Fetch real course data from backend
            const courseResponse = await courseAPI.getCourse(courseId);
            const courseData = courseResponse.data.data;

            if (!courseData) {
                setCourse(null);
                setSyllabus(null);
                setLoading(false);
                return;
            }

            // Set course data
            setCourse({
                ...courseData,
                highlights: [
                    `${courseData.questionCount || 0}+ Previous Year Questions`,
                    `${courseData.variationCount || 0} Question Variations`,
                    `${courseData.masterQuestionCount || 0} Master Question Patterns`,
                    'Video Lectures & Study Materials',
                    'Practice Tests & Mock Exams',
                    'Detailed Solution Explanations'
                ]
            });

            // Fetch real syllabus data
            try {
                const syllabusResponse = await syllabusAPI.getSyllabus(courseId);
                const syllabusData = syllabusResponse.data.data;

                if (syllabusData && syllabusData.units && syllabusData.units.length > 0) {
                    setSyllabus(syllabusData);
                } else {
                    // Syllabus exists but has no units - show "Coming Soon"
                    setSyllabus({
                        subject: courseData.name,
                        subjectCode: courseData.subjectCode || 'N/A',
                        fullMarks: courseData.fullMarks || 80,
                        units: []
                    });
                }
            } catch (syllabusError) {
                // Syllabus doesn't exist for this course - show "Coming Soon"
                console.log('Syllabus not available for this course');
                setSyllabus({
                    subject: courseData.name,
                    subjectCode: courseData.subjectCode || 'N/A',
                    fullMarks: courseData.fullMarks || 80,
                    units: []
                });
            }
        } catch (error) {
            console.error('Error fetching course:', error);
            toast.error('Failed to load course details');
            setCourse(null);
            setSyllabus(null);
        } finally {
            setLoading(false);
        }
    };

    const toggleUnit = (unitNumber) => {
        setExpandedUnits(prev => ({
            ...prev,
            [unitNumber]: !prev[unitNumber]
        }));
    };

    const handleAddToCart = () => {
        toast.success(`Added ${course.name} to cart!`);
    };

    const pricing = {
        yearly: 399,
        lifetime: 899
    };

    const originalPricing = {
        yearly: 599,
        lifetime: 1099  // lifetime price + 200
    };

    const currentPrice = pricing[durationType];
    const currentOriginalPrice = originalPricing[durationType];

    if (loading) {
        return (
            <div className="bg-white min-h-screen">
                <Navbar />
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="w-12 h-12 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!course || !syllabus) {
        return (
            <div className="bg-white min-h-screen">
                <Navbar />
                <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Course not found</h2>
                    <button onClick={() => navigate('/courses')} className="btn-primary px-6 py-3 rounded">
                        Back to Courses
                    </button>
                </div>
            </div>
        );
    }

    const discountPercent = Math.round(((currentOriginalPrice - currentPrice) / currentOriginalPrice) * 100);
    const totalQuestions = syllabus.units?.reduce((sum, unit) =>
        sum + (unit.topics?.reduce((topicSum, topic) => topicSum + (topic.questionCount || 0), 0) || 0), 0
    ) || 0;

    return (
        <div className="bg-white min-h-screen">
            <Navbar />

            {/* Breadcrumb */}<div className="bg-white border-b border-gray-200 pt-14">

            </div>


            {/* Hero - Simple & Clean */}
            <div className="bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <div className="max-w-7xl mx-auto  pb-8">
                        <button
                            onClick={() => navigate('/courses')}
                            className="text-sm text-blue-600 hover:underline font-medium"
                        >
                            ← Back
                        </button>
                    </div>

                    <div className="max-w-3xl">
                        <div className="flex items-center gap-2 mb-3 text-sm text-gray-300">
                            <span>{course.faculty}</span>
                            <span>•</span>
                            <span>Semester {course.semester}</span>
                            <span>•</span>
                            <span>{course.subjectCode}</span>
                        </div>
                        <h1 className="text-4xl font-bold mb-4 leading-tight">
                            {course.name}
                        </h1>
                        <div className="flex items-center gap-4 text-sm text-gray-300">
                            <span className="flex items-center gap-1">
                                <span className="text-yellow-400">★★★★★</span>
                                <span>4.8</span>
                            </span>
                            <span>•</span>
                            <span>{totalQuestions}+ questions</span>
                            <span>•</span>
                            <span>{syllabus.units?.length || 0} units</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Left: Syllabus */}
                    <div className="lg:col-span-2">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Subject Syllabus</h2>
                            <p className="text-sm text-gray-600">
                                {syllabus.units?.length || 0} units • {totalQuestions} questions • {course.fullMarks} marks
                            </p>
                        </div>

                        {syllabus.units && syllabus.units.length > 0 ? (
                            <div className="border border-gray-300 rounded">
                                {syllabus.units?.map((unit, idx) => {
                                    const unitQuestionCount = unit.topics?.reduce((sum, t) => sum + (t.questionCount || 0), 0) || 0;

                                    return (
                                        <div key={unit.unitNumber} className={idx !== 0 ? 'border-t border-gray-300' : ''}>
                                            <button
                                                onClick={() => toggleUnit(unit.unitNumber)}
                                                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <svg
                                                        className={`w-4 h-4 text-gray-600 transition-transform ${expandedUnits[unit.unitNumber] ? 'rotate-90' : ''}`}
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                    <div>
                                                        <div className="font-semibold text-gray-900">
                                                            Unit {unit.unitNumber}: {unit.title}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            {unit.topics?.length || 0} topics • {unitQuestionCount} questions
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>

                                            {expandedUnits[unit.unitNumber] && (
                                                <div className="px-4 pb-4 bg-gray-50">
                                                    <div className="pl-7 space-y-2">
                                                        {unit.topics?.map((topic, topicIdx) => (
                                                            <div
                                                                key={topicIdx}
                                                                className="flex items-start justify-between py-2 text-sm"
                                                            >
                                                                <div className="flex items-start gap-2 flex-1">
                                                                    <span className="font-medium text-blue-600 min-w-[3rem]">
                                                                        {topic.subtopicId}
                                                                    </span>
                                                                    <span className="text-gray-700">{topic.description}</span>
                                                                </div>
                                                                <span className="text-xs text-gray-500 ml-4">
                                                                    {topic.questionCount || 0} questions
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="border border-gray-300 rounded p-12 text-center bg-gray-50">
                                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Coming Soon</h3>
                                <p className="text-gray-600">
                                    Detailed syllabus for this course is being prepared and will be available shortly.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Right: Sticky Sidebar */}
                    <div className="lg:sticky lg:top-20 h-fit">
                        <div className="border border-gray-300 rounded shadow-lg">
                            {/* Image */}
                            <img
                                src={course.image}
                                alt={course.name}
                                className="w-full h-48 object-cover rounded-t"
                            />

                            <div className="p-6">
                                {/* Price */}
                                <div className="mb-6">
                                    <div className="flex items-baseline gap-2 mb-3">
                                        <span className="text-3xl font-bold text-gray-900">₹{currentPrice}</span>
                                        <span className="text-lg text-gray-500 line-through">₹{currentOriginalPrice}</span>
                                        <span className="text-sm font-semibold text-green-600">{discountPercent}% off</span>
                                    </div>

                                    {/* Duration Toggle */}
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setDurationType('yearly')}
                                            className={`flex-1 px-3 py-2 text-sm font-medium border rounded transition-colors ${durationType === 'yearly'
                                                ? 'border-blue-600 bg-blue-50 text-blue-600'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            1 Year
                                        </button>
                                        <button
                                            onClick={() => setDurationType('lifetime')}
                                            className={`flex-1 px-3 py-2 text-sm font-medium border rounded transition-colors ${durationType === 'lifetime'
                                                ? 'border-blue-600 bg-blue-50 text-blue-600'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            Lifetime
                                        </button>
                                    </div>
                                </div>

                                {/* Add to Cart Button */}
                                <button
                                    onClick={handleAddToCart}
                                    className="w-full bg-blue-600 text-white font-semibold py-3 rounded hover:bg-blue-700 transition-colors mb-6"
                                >
                                    Add to cart
                                </button>

                                {/* Course Includes */}
                                <div className="pt-6 border-t border-gray-200">
                                    <h3 className="font-bold text-gray-900 mb-4">This course includes:</h3>
                                    <ul className="space-y-3">
                                        {course.highlights?.map((highlight, idx) => (
                                            <li key={idx} className="flex items-start gap-3 text-sm">
                                                <svg className="w-4 h-4 text-gray-700 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-gray-700">{highlight}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-50 border-t border-gray-200 mt-16">
                <div className="max-w-7xl mx-auto px-6 py-8 text-center">
                    <p className="text-sm text-gray-600">© 2025 Exam Prasna. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default CourseDetail;
